import { Button, FormField, Notice, Table, Textarea } from "@smooth/design-system-components"
import { isAxiosError } from "axios"
import { useState } from "react"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { SMOOTH_SMOOTHER_API_URL } from "src/lib/constants"

type RowStatus = "success" | "skipped" | "failed" | "not_found"

interface Row {
  userId: number
  status: RowStatus
  reason: string | null
}

const COLUMNS = [
  { key: "userId", header: "User" },
  { key: "status", header: "Status" },
  { key: "reason", header: "Reason" },
]

/**
 * 複数人へのガルディア依頼。ユーザー ID をカンマ区切りで入れ、ユーザーごとの結果を出す。
 * 1 人 2 秒待つので人数が多いと時間がかかる（150 人弱まで）。
 */
export const GardiaRequest = () => {
  const { postWithToken } = useSmoothApi()
  const [userIds, setUserIds] = useState("")
  const [isRequesting, setIsRequesting] = useState(false)
  const [rows, setRows] = useState<Row[] | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleSubmit = async () => {
    setIsRequesting(true)
    setRows(null)
    setErrorMessage(null)
    try {
      // 依頼は外部 API を呼ぶ。今の設定では POST は再送されないが、再送する条件を広げたときも二重に依頼しないようにする
      const response = await postWithToken(
        `${SMOOTH_SMOOTHER_API_URL}/screen/gardia_requests`,
        { userIds },
        { "axios-retry": { retries: 0 } }
      )
      setRows((response.data as { results: Row[] }).results)
    } catch (e) {
      setErrorMessage(
        isAxiosError(e) && typeof e.response?.data?.message === "string"
          ? e.response.data.message
          : "依頼に失敗しました。依頼できたかどうかを簡易審査の結果で確認してください"
      )
    } finally {
      setIsRequesting(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {errorMessage && (
        <Notice tone="error" variant="card">
          {errorMessage}
        </Notice>
      )}
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault()
          handleSubmit()
        }}
      >
        <FormField label="user_id（カンマ区切りで）" hint="結果は各ユーザーの最新の簡易審査のガルディア結果に保存されます">
          <Textarea value={userIds} onChange={(e) => setUserIds(e.target.value)} placeholder="100,102,103" />
        </FormField>
        <div>
          <Button type="submit" disabled={isRequesting || userIds.trim() === ""}>
            {isRequesting ? "依頼中..." : "ガルディアAPIを叩く"}
          </Button>
        </div>
      </form>
      {rows && (
        <Table
          columns={COLUMNS}
          rows={rows.map((row) => ({ userId: row.userId, status: row.status, reason: row.reason ?? "" }))}
        />
      )}
    </div>
  )
}
